import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
  matchQuery,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryKey
} from "@tanstack/react-query";
import {
  PersistQueryClientProviderProps,
  persistQueryClientSave
} from "@tanstack/react-query-persist-client";

import { sharedStorage } from "@/store/tabStore/storage";

// Async persister on sharedStorage (non-namespaced) so the RQ cache stays shared across tabs.
export const localForagePersister = createAsyncStoragePersister({
  storage: sharedStorage,
  key: "react-query",
  throttleTime: 1000 // Throttle writes to reduce I/O operations
});

export const persistOptions: PersistQueryClientProviderProps["persistOptions"] =
  {
    persister: localForagePersister,
    maxAge: 24 * 60 * 60 * 1000,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => query.meta?.shouldPersist === true
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000 // 10 minutes
    }
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const mutationMeta =
            (mutation.meta?.invalidates as Array<QueryKey>) ?? [];
          return (
            mutationMeta?.some((queryKey: QueryKey) =>
              matchQuery({ queryKey }, query)
            ) ?? true
          );
        }
      });
    }
  }),
  queryCache: new QueryCache({
    onError: () => {} // Original error handler
  })
});

export const getPartnerDetailQueryKey = (appSubdomain: string) =>
  ["getPartnerDetail", appSubdomain] as const;

/**
 * Drops persisted queries matching `shouldRemove` from the IndexedDB snapshot,
 * keeping every other persisted query intact. Caller decides what to prune by
 * query key — e.g. partner detail at boot on non-partner hosts, where it is
 * never refetched and would otherwise show stale branding.
 *
 * Operates on storage directly: at boot the in-memory cache is still empty
 * (PersistQueryClientProvider has not restored yet), so dehydrating the
 * client here would overwrite the snapshot with nothing.
 */
export const removePersistedQueries = async (
  shouldRemove: (queryKey: QueryKey) => boolean
) => {
  try {
    const persisted = await localForagePersister.restoreClient();
    if (!persisted) return;

    await localForagePersister.persistClient({
      ...persisted,
      clientState: {
        ...persisted.clientState,
        queries: persisted.clientState.queries.filter(
          (query) => !shouldRemove(query.queryKey)
        )
      }
    });
  } catch (error) {
    // Boot must not crash over cache hygiene; worst case the stale entry
    // survives until the persister maxAge expires it
    console.error("Failed to prune persisted queries:", error);
  }
};

export const clearUserQueryCache = async (
  client: QueryClient,
  appSubdomain: string,
  {
    shouldPreservePartnerDetail = true
  }: { shouldPreservePartnerDetail?: boolean } = {}
) => {
  const partnerDetailQueryKey = getPartnerDetailQueryKey(appSubdomain);

  client.removeQueries({
    predicate: (query) =>
      !shouldPreservePartnerDetail ||
      !matchQuery({ queryKey: partnerDetailQueryKey, exact: true }, query)
  });

  client.getMutationCache().clear();

  if (!shouldPreservePartnerDetail) {
    await localForagePersister.removeClient();
    return;
  }

  await persistQueryClientSave({
    queryClient: client,
    persister: localForagePersister,
    dehydrateOptions: persistOptions.dehydrateOptions
  });
};
